import { getApiUrl, getHeaders } from '../config/api';
import { getValidToken, handleTokenExpiration } from '../utils/authUtils';

class PriceService {
  constructor() {
    this.baseUrl = getApiUrl('/api/admin');
  }

  // Get authentication token from localStorage with validation
  getAuthToken() {
    const token = getValidToken();
    console.log('Retrieved token for price service:', token ? 'Token exists and valid' : 'No valid token found');
    return token;
  }

  // Get all price items
  async getPrices() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching prices from:', `${this.baseUrl}/prices`);
      console.log('Using token:', token.substring(0, 20) + '...');

      const response = await fetch(`${this.baseUrl}/prices`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Prices response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication failed while fetching prices, handling token expiration');
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received prices data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching prices:', error);
      throw error;
    }
  }

  // Get all scrap items (admin)
  async getScrapItems() {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Fetching scrap items from:', `${this.baseUrl}/scrap/item`);

      const response = await fetch(`${this.baseUrl}/scrap/item`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      console.log('Get scrap items response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching scrap items:', error);
      throw error;
    }
  }

  // Get prices by category
  async getPricesByCategory(category) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const response = await fetch(`${this.baseUrl}/prices/category/${category}`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching prices by category:', error);
      throw error;
    }
  }

  // Add new price item
  async addPrice(priceData) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log('Adding new price item:', priceData);

      const response = await fetch(`${this.baseUrl}/prices`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(priceData)
      });

      console.log('Add price response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Price added successfully:', data);
      return data;
    } catch (error) {
      console.error('Error adding price:', error);
      throw error;
    }
  }

  // Add new scrap item (admin)
  async addScrapItem(itemData) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const payload = {
        id: itemData.id,
        category: itemData.category,
        name: itemData.name,
        unit: itemData.unit || 'kg',
        price: parseFloat(itemData.price),
        description: itemData.description || '',
        active: itemData.active !== false,
      };

      console.log('Adding new scrap item:', payload);

      const response = await fetch(`${this.baseUrl}/scrap/item`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(payload)
      });

      console.log('Add scrap item response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Scrap item added successfully:', data);
      return data;
    } catch (error) {
      console.error('Error adding scrap item:', error);
      throw error;
    }
  }

  // Update existing price item
  async updatePrice(id, priceData) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log(`Updating price item ${id}:`, priceData);

      const response = await fetch(`${this.baseUrl}/prices/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(priceData)
      });

      console.log('Update price response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Price updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating price:', error);
      throw error;
    }
  }

  // Update scrap item (admin)
  async updateScrapItem(id, itemData) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const payload = {
        category: itemData.category,
        name: itemData.name,
        unit: itemData.unit || 'kg',
        price: parseFloat(itemData.price),
        description: itemData.description || '',
        active: itemData.active !== false,
      };

      console.log(`Updating scrap item ${id}:`, payload);

      const response = await fetch(`${this.baseUrl}/scrap/item/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(payload)
      });

      console.log('Update scrap item response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Scrap item updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error updating scrap item:', error);
      throw error;
    }
  }

  // Delete price item
  async deletePrice(id) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log(`Deleting price item ${id}`);

      const response = await fetch(`${this.baseUrl}/prices/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token),
      });

      console.log('Delete price response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Price deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('Error deleting price:', error);
      throw error;
    }
  }

  // Delete scrap item (admin)
  async deleteScrapItem(id) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      console.log(`Deleting scrap item ${id}`);

      const response = await fetch(`${this.baseUrl}/scrap/item/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token),
      });

      console.log('Delete scrap item response status:', response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      // API returns a success message as text
      const text = await response.text();
      return { success: true, message: text || 'Deleted successfully' };
    } catch (error) {
      console.error('Error deleting scrap item:', error);
      throw error;
    }
  }

  // Bulk update prices
  async bulkUpdatePrices(priceUpdates) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const response = await fetch(`${this.baseUrl}/prices/bulk-update`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify({ updates: priceUpdates })
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error bulk updating prices:', error);
      throw error;
    }
  }

  // Get price history for an item
  async getPriceHistory(id) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const response = await fetch(`${this.baseUrl}/prices/${id}/history`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching price history:', error);
      throw error;
    }
  }

  // Export prices to CSV
  async exportPrices(format = 'csv') {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const response = await fetch(`${this.baseUrl}/prices/export?format=${format}`, {
        method: 'GET',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting prices:', error);
      throw error;
    }
  }

  // Import prices from CSV
  async importPrices(file) {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${this.baseUrl}/prices/import`, {
        method: 'POST',
        headers: {
          ...getHeaders(token),
          // Remove Content-Type header to let browser set it for FormData
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          handleTokenExpiration();
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing prices:', error);
      throw error;
    }
  }
}

const priceService = new PriceService();
export default priceService;

